import type { NotificationData } from '../../../services/notification.service';
import { enrichGetLpImagePayloadForPrepaidRedeemBootstrap } from '../../../shared/utils/get-lp-image-prepaid-redeem.util';
import type { MultiEligiblePurchaseOption } from '../models/multi-eligible.models';

/**
 * CWS-1915: a prepaid book chosen in the Active subscription chooser is redeemed on the
 * Redeem Prepaid Purchase screen, not inside the chooser.
 *
 * That screen is the one that captures who came in: plate (required, prefilled), name,
 * phone and email, recorded on the redemption (CWS-1881). The chooser's own wash list
 * posted the redemption with none of that. So once the attendant picks the book, the
 * chooser hands off to the create-order modal in prepaid redeem mode, the same entry a QR
 * scan or a plate match uses, and that screen finishes the job.
 */

export const PREPAID_REDEEM_HANDOFF_MESSAGE = 'Active prepaid wash book found. Prompt redemption.';

/**
 * Fields that describe the chooser itself and must not ride along into the redeem screen.
 *
 * Nothing that describes where the scan CAME FROM belongs here. `is_lpr_upload` in
 * particular: it marks a staff image upload, and the redeem screen reads it to decide
 * whether to offer the location and lane pickers (`isUiLprImageUploadNotificationData`).
 * Dropping it made a hand-off look like a camera lane read, so those pickers vanished and
 * the scanned lane was reused — the attendant could no longer correct either.
 */
const CHOOSER_ONLY_FIELDS = [
  'purchase_options',
  'selection_kind',
  'rewash_choice_required',
  'rewash_eligibility',
  'membership_rewash_eligibility',
  'prepaid_rewash_eligibility',
  'cross_owner',
  'event',
] as const;

export interface PrepaidRedeemHandoffContext {
  /** How the chooser was opened: a lane scan, or an attendant's phone look-up. */
  mode: 'lpr' | 'phone';
  /** The lane notification the chooser opened with; its scan context is carried over. */
  laneData?: Record<string, any> | null;
  customerId?: string | null;
  vehicleId?: string | null;
  locationId?: string | null;
  laneId?: string | null;
}

function str(value: unknown): string {
  return value != null ? String(value).trim() : '';
}

function withoutChooserFields(data: Record<string, any>): Record<string, any> {
  const next = { ...data };
  for (const key of CHOOSER_ONLY_FIELDS) {
    delete next[key];
  }
  return next;
}

/**
 * The create-order-request notification that opens Redeem Prepaid Purchase for `option`, or
 * null when the book, customer or vehicle is unknown and there is nothing to hand off.
 */
export function buildPrepaidRedeemHandoffNotification(
  option: MultiEligiblePurchaseOption,
  context: PrepaidRedeemHandoffContext
): NotificationData | null {
  const prepaidPurchaseId = str(option.prepaid_purchase_id);
  if (!prepaidPurchaseId || option.redeem_event !== 'redeem-prepaid-purchase') {
    return null;
  }
  const lane = context.laneData ?? {};
  const customerId = str(option.customer_id) || str(context.customerId) || str(lane['customer_id']);
  const vehicleId = str(option.vehicle_id) || str(context.vehicleId) || str(lane['vehicle_id']);

  if (context.mode === 'lpr') {
    // Same bootstrap a lane's redeem-prepaid-purchase event gets: the scan's plate, lane and
    // LPR entry stay on the request, so the redemption is stamped against the visit.
    const data = enrichGetLpImagePayloadForPrepaidRedeemBootstrap({
      ...withoutChooserFields(lane),
      prepaid_purchase_id: prepaidPurchaseId,
      customer_id: customerId || undefined,
      vehicle_id: vehicleId || undefined,
      event: 'redeem-prepaid-purchase',
      source_context: 'lpr',
      chooser_handoff: true,
    });
    return {
      type: 'create-order-request',
      message: PREPAID_REDEEM_HANDOFF_MESSAGE,
      timestamp: new Date(),
      data,
    };
  }

  if (!customerId || !vehicleId) {
    return null;
  }
  const locationId = str(context.locationId);
  const laneId = str(context.laneId);
  return {
    type: 'create-order-request',
    message: PREPAID_REDEEM_HANDOFF_MESSAGE,
    timestamp: new Date(),
    data: {
      prepaid_purchase_id: prepaidPurchaseId,
      customer_id: customerId,
      vehicle_id: vehicleId,
      purchase_vehicle_id: vehicleId,
      ...(locationId ? { location_id: locationId } : {}),
      ...(laneId ? { lane_id: laneId } : {}),
      // `qr_scan` is what the modal keys a "found by look-up, not by camera" bootstrap on
      // (see app.component's QR path); the source names the real origin.
      qr_scan: true,
      source_context: 'phone_lookup',
      chooser_handoff: true,
    },
  };
}
